import { inject, Injectable, InjectionToken } from '@angular/core';

import type { RealtimeConnectionState } from './realtime-state.model';

export type RealtimeAttemptBucket = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type RealtimeDelayBucket = '2s' | '4s' | '8s' | '16s' | '30s';
export type RealtimeDurationBucket =
  | 'under-250ms'
  | '250ms-to-1s'
  | '1s-to-5s'
  | '5s-or-more';
export type RealtimeNegotiationOutcome =
  | 'succeeded'
  | 'unauthorized'
  | 'forbidden'
  | 'unavailable'
  | 'network'
  | 'invalid-contract';
export type RealtimeConnectionOutcome = 'succeeded' | 'failed' | 'cancelled';
export type RealtimeReconnectOutcome =
  | 'succeeded'
  | 'failed'
  | 'exhausted'
  | 'cancelled';
export type RealtimeMessageRejectionReason =
  | 'malformed'
  | 'unsupported-version'
  | 'client-mismatch'
  | 'day-mismatch'
  | 'no-authoritative-baseline'
  | 'stale-or-duplicate';
export type RealtimeAuthoritativeRefreshOutcome =
  | 'succeeded'
  | 'failed'
  | 'invalid-result'
  | 'cancelled';
export type RealtimeTelemetryEvent =
  | 'state-transition'
  | 'negotiation-outcome'
  | 'connection-attempt'
  | 'connection-outcome'
  | 'reconnect-attempt'
  | 'reconnect-outcome'
  | 'message-rejection'
  | 'authoritative-refresh-outcome';

export interface RealtimeTelemetryRecord {
  readonly event: RealtimeTelemetryEvent;
  readonly correlationId: string;
  readonly from?: RealtimeConnectionState;
  readonly to?: RealtimeConnectionState;
  readonly outcome?:
    | RealtimeNegotiationOutcome
    | RealtimeConnectionOutcome
    | RealtimeReconnectOutcome
    | RealtimeAuthoritativeRefreshOutcome;
  readonly attempt?: RealtimeAttemptBucket;
  readonly delay?: RealtimeDelayBucket;
  readonly duration?: RealtimeDurationBucket;
  readonly reason?: RealtimeMessageRejectionReason;
}

export abstract class RealtimeTelemetrySink {
  abstract emit(record: RealtimeTelemetryRecord): void | Promise<void>;
}

export const REALTIME_TELEMETRY_SINK = new InjectionToken<RealtimeTelemetrySink>(
  'REALTIME_TELEMETRY_SINK',
);

export const REALTIME_CORRELATION_ID_FACTORY = new InjectionToken<() => string>(
  'REALTIME_CORRELATION_ID_FACTORY',
  { factory: () => () => crypto.randomUUID() },
);

@Injectable()
export class RealtimeTelemetry {
  private readonly sink = inject(REALTIME_TELEMETRY_SINK);
  private readonly correlationId = inject(REALTIME_CORRELATION_ID_FACTORY)();

  stateTransition(from: RealtimeConnectionState, to: RealtimeConnectionState): void {
    if (from === to) return;
    this.emit({ event: 'state-transition', correlationId: this.correlationId, from, to });
  }

  negotiationOutcome(
    outcome: RealtimeNegotiationOutcome,
    duration: RealtimeDurationBucket,
  ): void {
    this.emit({ event: 'negotiation-outcome', correlationId: this.correlationId, outcome, duration });
  }

  connectionAttempt(attempt: RealtimeAttemptBucket): void {
    this.emit({ event: 'connection-attempt', correlationId: this.correlationId, attempt });
  }

  connectionOutcome(
    outcome: RealtimeConnectionOutcome,
    duration: RealtimeDurationBucket,
  ): void {
    this.emit({ event: 'connection-outcome', correlationId: this.correlationId, outcome, duration });
  }

  reconnectAttempt(attempt: RealtimeAttemptBucket, delay: RealtimeDelayBucket): void {
    this.emit({ event: 'reconnect-attempt', correlationId: this.correlationId, attempt, delay });
  }

  reconnectOutcome(
    outcome: RealtimeReconnectOutcome,
    duration: RealtimeDurationBucket,
  ): void {
    this.emit({ event: 'reconnect-outcome', correlationId: this.correlationId, outcome, duration });
  }

  messageRejected(reason: RealtimeMessageRejectionReason): void {
    this.emit({ event: 'message-rejection', correlationId: this.correlationId, reason });
  }

  authoritativeRefreshOutcome(
    outcome: RealtimeAuthoritativeRefreshOutcome,
    duration: RealtimeDurationBucket,
  ): void {
    this.emit({
      event: 'authoritative-refresh-outcome',
      correlationId: this.correlationId,
      outcome,
      duration,
    });
  }

  private emit(record: RealtimeTelemetryRecord): void {
    const frozenRecord = Object.freeze(record);
    try {
      const result = this.sink.emit(frozenRecord);
      if (result instanceof Promise) void result.catch(() => undefined);
    } catch {
      // Telemetry is deliberately isolated from the realtime lifecycle.
    }
  }
}

export class ConsoleRealtimeTelemetrySink extends RealtimeTelemetrySink {
  override emit(record: RealtimeTelemetryRecord): void {
    console.info('[realtime]', record);
  }
}